/**
 * EventBus.ts
 * Implémentation concrète de IEventBus pour découpler les services
 * Pattern Observer/Mediator pour la communication entre services
 */
import { logger } from '../utils/ProfessionalLogger';
import type {
  IEventBus,
  AppEventType,
  AppEvent,
  AppEventListener,
} from '../types/manager-interfaces';

/**
 * Implémentation singleton de l'EventBus
 * Permet la communication découplée entre services via événements
 */
export class EventBus implements IEventBus {
  private static instance: EventBus | null = null;
  private listeners: Map<AppEventType, Set<AppEventListener>> = new Map();
  private eventHistory: AppEvent[] = [];
  private maxHistorySize: number = 100;

  private constructor() {
    // Singleton privé
  }

  /**
   * Obtient l'instance singleton de l'EventBus
   */
  public static getInstance(): EventBus {
    if (!EventBus.instance) {
      EventBus.instance = new EventBus();
      logger.info('📡 EventBus initialisé (singleton)');
    }
    return EventBus.instance;
  }

  /**
   * S'abonne à un type d'événement
   * @param eventType - Le type d'événement à écouter
   * @param listener - La fonction callback à appeler
   */
  public on<T = unknown>(eventType: AppEventType, listener: AppEventListener<T>): void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.listeners.get(eventType)!.add(listener as AppEventListener);
    logger.debug(`📡 Listener ajouté pour l'événement: ${eventType}`);
  }

  /**
   * Se désabonne d'un type d'événement
   * @param eventType - Le type d'événement
   * @param listener - La fonction callback à retirer
   */
  public off<T = unknown>(eventType: AppEventType, listener: AppEventListener<T>): void {
    const eventListeners = this.listeners.get(eventType);
    if (eventListeners) {
      eventListeners.delete(listener as AppEventListener);
      if (eventListeners.size === 0) {
        this.listeners.delete(eventType);
      }
      logger.debug(`📡 Listener retiré pour l'événement: ${eventType}`);
    }
  }

  /**
   * Émet un événement à tous les listeners abonnés
   * @param event - L'événement à émettre
   */
  public emit<T = unknown>(event: AppEvent<T>): void {
    const eventListeners = this.listeners.get(event.type);

    if (eventListeners && eventListeners.size > 0) {
      // Ajouter timestamp si absent
      if (!event.timestamp) {
        event.timestamp = Date.now();
      }

      // Ajouter à l'historique
      this.eventHistory.push(event);
      if (this.eventHistory.length > this.maxHistorySize) {
        this.eventHistory.shift();
      }

      // Notifier tous les listeners
      eventListeners.forEach(listener => {
        try {
          listener(event);
        } catch (error) {
          logger.error(`❌ Erreur dans le listener pour ${event.type}:`, error);
        }
      });

      logger.debug(`📡 Événement émis: ${event.type}`, { data: event.data });
    } else {
      logger.debug(`📡 Événement émis sans listeners: ${event.type}`);
    }
  }

  /**
   * Vide tous les listeners et l'historique
   */
  public clear(): void {
    this.listeners.clear();
    this.eventHistory = [];
    logger.info('🧹 EventBus vidé');
  }

  /**
   * Obtient l'historique des événements (pour debug)
   */
  public getEventHistory(): AppEvent[] {
    return [...this.eventHistory];
  }

  /**
   * Obtient le nombre de listeners pour un type d'événement
   */
  public getListenerCount(eventType: AppEventType): number {
    return this.listeners.get(eventType)?.size || 0;
  }

  /**
   * Détruit l'instance singleton (pour les tests)
   */
  public static destroy(): void {
    if (EventBus.instance) {
      EventBus.instance.clear();
      EventBus.instance = null;
      logger.info('🧹 EventBus détruit');
    }
  }
}
